/**
 * Format canonical property manager contact for public display.
 *
 * - Frontend: `@merlot/common/propertyManagerFormatting.js`
 * - Backend: mirror in `backend/src/utils/propertyManagerFormatting.js`
 */

/**
 * @param {object|null|undefined} address
 * @returns {string}
 */
export function formatManagerAddressOneLine(address) {
  if (!address || typeof address !== 'object') return '';
  const street = String(address.street || '').trim();
  const street2 = String(address.street2 || '').trim();
  const postalCode = String(address.postalCode || '').trim();
  const locality = String(address.locality || '').trim();

  const line1 = [street, street2].filter(Boolean).join(', ');
  const line2 = [postalCode, locality].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ');
}

/**
 * @param {object|null|undefined} address
 * @returns {string[]}
 */
export function formatManagerAddressLines(address) {
  const one = formatManagerAddressOneLine(address);
  if (!one) return [];
  return one.split(', ').length > 1 ? one.split(', ') : [one];
}

/**
 * @param {string} phone
 * @returns {string}
 */
export function telHref(phone) {
  const t = String(phone || '').trim();
  if (!t) return '';
  const digits = t.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '';
}

/**
 * @param {string} email
 * @returns {string}
 */
export function mailtoHref(email) {
  const t = String(email || '').trim();
  return t ? `mailto:${t}` : '';
}

/**
 * @param {object|null|undefined} manager
 */
export function normalizeManagerWire(manager) {
  const m = manager && typeof manager === 'object' ? manager : {};
  const address = m.address && typeof m.address === 'object' ? m.address : {};
  return {
    companyName: String(m.companyName || '').trim(),
    phone: String(m.phone || '').trim(),
    email: String(m.email || '').trim(),
    address: {
      street: String(address.street || '').trim(),
      street2: String(address.street2 || '').trim(),
      locality: String(address.locality || '').trim(),
      postalCode: String(address.postalCode || '').trim()
    },
    addressLine: formatManagerAddressOneLine(address)
  };
}

/**
 * True when any public contact field is non-empty.
 * Empty `{}` from SSG/Mongo defaults is not usable contact.
 */
export function hasManagerContact(manager) {
  const n = normalizeManagerWire(manager);
  return Boolean(n.companyName || n.email || n.phone || n.addressLine);
}

/**
 * @param {object|null|undefined} data GET /v1/property/:locale body
 * @returns {object|null} raw `property.manager` or null
 */
export function managerFromPropertyPayload(data) {
  const m = data?.property?.manager;
  return m && typeof m === 'object' ? m : null;
}
